/**
 * The shared bind-frame check (ADR-224 §4.4) — does each bone of a body carry
 * the family anchor's frame CONVENTION? One pure function, written to be
 * ported line for line: World runs it in the intake report and the NEXUS
 * deliver gate, Harry runs it before delivery, and both pin the numbers in
 * `bind-frame-check.expected.json` (same file, same corpus).
 *
 * WHY THIS MEASURE. World binds clips as ABSOLUTE local rotations by bone
 * name (VRMAvatarDriver bindAnimationDirect). A clip therefore reproduces the
 * anchor's pose bone by bone in WORLD, and a body's mesh follows it only if
 * each bone's rest frame sits on the flesh the way the anchor's does: the
 * same local axis running down the bone, and the same local roll about it,
 * judged against the body's own anatomy (forward / up). A parent-relative
 * (local) comparison cannot see that: a rolled parent and a child rolled the
 * same way leave the child's LOCAL rest identical to the anchor's, yet the
 * clip overwrites the parent's local and both bones twist in world. The
 * corpus test beside this file shows the cancellation on the Tripo presets.
 *
 * INPUTS (skeleton facts only, rig world rest):
 *   per bone   R = world rest rotation (unit quaternion, x y z w)
 *              p = world rest position; c = world rest position of the
 *              bone's primary child (BBSA `primaryChild`), the bone's axis
 *   per body   F = anatomical forward, U = anatomical up, in rig world (the
 *              hub triad, `hubToWorld(rig)` of (0,0,1) and (0,1,0) — the same
 *              facing #185's authored-frame guard measures against)
 *
 * THE FORMULA, per bone, computed identically on the body and on the anchor:
 *   anatomical basis   f = |F|,  u = |U − (U·f) f|,  l = u × f   (l = the body's left)
 *   bone axis          d = |c − p|                                (world)
 *   anatomical axis    â = (d·l, d·u, d·f)          — where the bone points on the body
 *   local axis         ℓ = R⁻¹ d                    — which local axis runs down the bone
 *   reference          r = f (anatomical forward), unless the bone lies within
 *                      30° of ±forward on EITHER skeleton (|r − (r·d) d| < ½,
 *                      i.e. √(1 − â_z²) < sin 30°, on body or anchor) — then
 *                      r = u (anatomical up) under the same test; if both
 *                      fail, roll is UNMEASURED (never a silent pass). Limbs,
 *                      spine and neck always take forward; a foot takes up
 *   local reference    ρ = R⁻¹ |r − (r·d) d|        — "forward", seen from inside the bone
 *
 *   AXIS°  = ∠(ℓ_body, ℓ_anchor). Pose-independent: an A-posed and a T-posed
 *            body with the same convention read 0 (the bone runs down the same
 *            local axis whatever the pose).
 *   ROLL°  = align the axes, then compare the references about the axis:
 *            Q = the shortest-arc rotation carrying ℓ_anchor onto ℓ_body;
 *            ρ′ = Q ρ_anchor, re-orthogonalised against ℓ_body;
 *            roll = atan2( ℓ_body · (ρ_body × ρ′), ρ_body · ρ′ )   ∈ (−180, 180]
 *            In world words: carry the anchor's local forward into this
 *            bone's frame (s = R_body ρ′), and measure s against the body's
 *            forward projected perpendicular to the bone, signed about the
 *            bone axis parent→child.
 *            SIGN: positive = the body's bone frame is rotated +roll
 *            (right-hand rule about parent→child) relative to the anchor's
 *            convention. (Proof: R_body = R_anchor·Rot(ℓ, θ) ⇒ ρ_body =
 *            Rot(ℓ, −θ) ρ_anchor ⇒ roll = θ.)
 *            A vs T pose is swing: it moves â, not ρ — abduction in the
 *            coronal plane leaves forward perpendicular to the arm and fixed
 *            in the bone's frame, so it reads 0 here.
 *   DIRECTION° = ∠(â_body, â_anchor) — the pose difference (A vs T). REPORTED,
 *            never judged: the clip overwrites it.
 *   ORDER: roll is measured only when the axis test passes (AXIS° ≤ 30).
 *            A bone running down a different local axis is already a finding,
 *            and aligning axes 90–180° apart makes the roll ill-conditioned
 *            (no unique shortest arc at 180°) — reported null, never guessed.
 *   Degenerate: no child / co-located child (≤ 1e-6 rig units) → the bone is
 *            not measured and is listed in `missing`.
 *
 * VERDICT per bone: AXIS° > 30 → 'axis'; else |ROLL°| > 30 → 'roll'; else
 * 'ok'; roll unmeasured → 'unmeasured'. The body's verdict is 'bind-frames'
 * when any checked bone is 'axis' or 'roll', 'unmeasured' when no bone could
 * be measured, else 'ok'. 30° = ADR-224's FRAME_LIMIT_DEG.
 *
 * Depends on: math/quat, math/vec3.
 */
import { quatInvertUnit, quatNormalize, quatRotateVec3, type Quat } from '../math/quat.js';
import { vec3Cross, vec3Dot, vec3Length, vec3Normalize, vec3Scale, vec3Sub, type Vec3Data } from '../math/vec3.js';

export const BIND_FRAME_LIMIT_DEG = 30;
/** The reference must stand at least 30° off the bone: √(1 − cos²) ≥ sin 30°. */
const REFERENCE_CONDITIONING = 0.5;
/** Co-location floor for the child offset, rig units. */
const MIN_BONE_LENGTH = 1e-6;
const DEG = 180 / Math.PI;

/** The body's anatomical frame in rig world. */
export interface AnatomicalFrame {
  readonly forward: Vec3Data;
  readonly up: Vec3Data;
}

/** One bone's world rest facts. */
export interface BoneRest {
  /** World rest rotation (unit, x y z w). */
  readonly rotation: Quat;
  readonly position: Vec3Data;
  /** World rest position of the primary child; null = leaf. */
  readonly childPosition: Vec3Data | null;
}

/**
 * A bone's frame facts, the pose-independent part expressed in the bone's own
 * local frame and the pose part in the anatomical frame. This is what an
 * anchor REFERENCE stores (serialisable — World pins these numbers).
 */
export interface BoneFrameFacts {
  /** ℓ — the local axis running down the bone (unit, bone-local). */
  readonly localAxis: Vec3Data;
  /** ρ for r = forward (unit, bone-local, ⊥ ℓ); null when the bone lies within 30° of ±forward. */
  readonly localForward: Vec3Data | null;
  /** ρ for r = up (unit, bone-local, ⊥ ℓ); null when the bone lies within 30° of ±up. */
  readonly localUp: Vec3Data | null;
  /** â — the bone direction in the anatomical frame (left, up, forward). */
  readonly anatomicalAxis: Vec3Data;
}

export type BoneFrameVerdict = 'ok' | 'axis' | 'roll' | 'unmeasured';

export interface BoneFrameReading {
  readonly axisDeg: number;
  /** Signed; null when no reference is conditioned or the axis test fired. */
  readonly rollDeg: number | null;
  readonly directionDeg: number;
  /** Which anatomical reference the roll was measured against. */
  readonly reference: 'forward' | 'up' | null;
  readonly verdict: BoneFrameVerdict;
}

export type BindFrameVerdict = 'ok' | 'bind-frames' | 'unmeasured';

export interface BindFrameReport {
  /** Keyed like the inputs (BBSA ontology ids by convention). */
  readonly bones: Readonly<Record<string, BoneFrameReading>>;
  /** Anchor bones the body could not supply (absent or leaf). */
  readonly missing: readonly string[];
  readonly verdict: BindFrameVerdict;
}

function perpendicular(v: Vec3Data, axis: Vec3Data): Vec3Data {
  return vec3Sub(v, vec3Scale(axis, vec3Dot(v, axis)));
}

function angleDeg(a: Vec3Data, b: Vec3Data): number {
  return Math.acos(Math.max(-1, Math.min(1, vec3Dot(a, b)))) * DEG;
}

/**
 * Rotate `v` by the shortest arc carrying unit `from` onto unit `to`
 * (Rodrigues). Only called with the axes ≤ 30° apart, so the arc is unique;
 * parallel axes carry `v` unchanged.
 */
function alongShortestArc(v: Vec3Data, from: Vec3Data, to: Vec3Data): Vec3Data {
  const cos = vec3Dot(from, to);
  const k = vec3Cross(from, to);
  const sin = vec3Length(k);
  if (sin < 1e-9) return v;
  const axis = vec3Scale(k, 1 / sin);
  const kv = vec3Cross(axis, v);
  const kd = vec3Dot(axis, v) * (1 - cos);
  return { x: v.x * cos + kv.x * sin + axis.x * kd, y: v.y * cos + kv.y * sin + axis.y * kd, z: v.z * cos + kv.z * sin + axis.z * kd };
}

/**
 * The frame facts of one bone against its skeleton's anatomical frame, or null
 * when the bone has no measurable axis (leaf or co-located child).
 */
export function boneFrameFacts(bone: BoneRest, frame: AnatomicalFrame): BoneFrameFacts | null {
  if (bone.childPosition === null) return null;
  const offset = vec3Sub(bone.childPosition, bone.position);
  if (vec3Length(offset) <= MIN_BONE_LENGTH) return null;
  const d = vec3Normalize(offset);
  const f = vec3Normalize(frame.forward);
  const u = vec3Normalize(perpendicular(frame.up, f));
  const l = vec3Cross(u, f);
  const toLocal = quatInvertUnit(quatNormalize(bone.rotation));
  const local = (r: Vec3Data): Vec3Data | null => {
    const p = perpendicular(r, d);
    return vec3Length(p) < REFERENCE_CONDITIONING ? null : vec3Normalize(quatRotateVec3(toLocal, vec3Normalize(p)));
  };
  return {
    localAxis: vec3Normalize(quatRotateVec3(toLocal, d)),
    localForward: local(f),
    localUp: local(u),
    anatomicalAxis: { x: vec3Dot(d, l), y: vec3Dot(d, u), z: vec3Dot(d, f) },
  };
}

/** Compare one body bone's frame facts with the anchor's (the formula in the header). */
export function compareBoneFrame(body: BoneFrameFacts, anchor: BoneFrameFacts): BoneFrameReading {
  const axisDeg = angleDeg(body.localAxis, anchor.localAxis);
  const directionDeg = angleDeg(body.anatomicalAxis, anchor.anatomicalAxis);
  const reference: 'forward' | 'up' | null =
    body.localForward !== null && anchor.localForward !== null ? 'forward' : body.localUp !== null && anchor.localUp !== null ? 'up' : null;
  let rollDeg: number | null = null;
  if (reference !== null && axisDeg <= BIND_FRAME_LIMIT_DEG) {
    const rhoBody = (reference === 'forward' ? body.localForward : body.localUp)!;
    const rhoAnchor = (reference === 'forward' ? anchor.localForward : anchor.localUp)!;
    // ρ_anchor ⊥ ℓ_anchor, so the carried vector is ⊥ ℓ_body up to rounding; re-orthogonalise.
    const s = vec3Normalize(perpendicular(alongShortestArc(rhoAnchor, anchor.localAxis, body.localAxis), body.localAxis));
    rollDeg = Math.atan2(vec3Dot(body.localAxis, vec3Cross(rhoBody, s)), vec3Dot(rhoBody, s)) * DEG;
  }
  const verdict: BoneFrameVerdict =
    axisDeg > BIND_FRAME_LIMIT_DEG ? 'axis' : rollDeg === null ? 'unmeasured' : Math.abs(rollDeg) > BIND_FRAME_LIMIT_DEG ? 'roll' : 'ok';
  return { axisDeg, rollDeg, directionDeg, reference, verdict };
}

/** An anchor's per-bone reference: its frame facts against its own anatomical frame. */
export function bindFrameReference(bones: Readonly<Record<string, BoneRest>>, frame: AnatomicalFrame): Record<string, BoneFrameFacts> {
  const out: Record<string, BoneFrameFacts> = {};
  for (const [key, bone] of Object.entries(bones)) {
    const facts = boneFrameFacts(bone, frame);
    if (facts !== null) out[key] = facts;
  }
  return out;
}

/**
 * The check: every bone the anchor references, measured on the body against
 * the body's own anatomical frame and compared with the anchor.
 */
export function checkBindFrames(
  body: Readonly<Record<string, BoneRest>>,
  frame: AnatomicalFrame,
  anchor: Readonly<Record<string, BoneFrameFacts>>,
): BindFrameReport {
  const bones: Record<string, BoneFrameReading> = {};
  const missing: string[] = [];
  for (const [key, ref] of Object.entries(anchor)) {
    const rest = body[key];
    const facts = rest === undefined ? null : boneFrameFacts(rest, frame);
    if (facts === null) {
      missing.push(key);
      continue;
    }
    bones[key] = compareBoneFrame(facts, ref);
  }
  const readings = Object.values(bones);
  const verdict: BindFrameVerdict = readings.some((r) => r.verdict === 'axis' || r.verdict === 'roll')
    ? 'bind-frames'
    : readings.some((r) => r.verdict === 'ok')
      ? 'ok'
      : 'unmeasured';
  return { bones, missing, verdict };
}
